import classNames from 'classnames';
import Hls from 'hls.js';
import round from 'lodash/round';
import _throttle from 'lodash/throttle';
import mux from 'mux-embed';
import playerjs from 'player.js';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  updateShareVideoIframeLoaded,
  updateShareVideoPlayerReady,
} from 'actions/shareVideo';
import {
  requestVideoPlayerPause,
  setCurrentCaption,
  updateChromePipEnabled,
  updatePipSupported,
  updateSafariPipEnabled,
  updateVideoPlayerEnded,
  updateVideoPlayerSourceFromClip,
  videoReadyToPlayOnSharePage,
} from 'actions/videoPlayer';
import {
  CLIP_VIDEO_SOURCE_URL_UPDATED,
  PLAYBACK_RATE_CHANGE,
  VIDEO_PLAYER_VIDEO_PLAY_START,
  VIDEO_PLAYER_WILL_MUTE_FILLER_WORDS,
} from 'constants/events';
import {
  CONFIG_HLS_JS_PLAYER,
  ROLLOUT_WAVEFORM,
  ROLLOUT_WEAVE,
  SEND_HLS_PLAYBACK_STATS,
  VARIANT,
} from 'constants/featureFlag';
import { SHARE_VIDEO_EL_LOADED } from 'constants/messages';
import { DASH, M3U8, WEBM } from 'constants/mimes';
import { V6 } from 'constants/recordingVersions';
import { MUX_ENV_KEY } from 'constants/videoPlaybackStats';
import { TEXT_AUDIO } from 'constants/videoTranscript';
import { selectUserId } from 'selectors/user';
import { selectIsMainVideoOnSharePage } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import {
  createDashInstance,
  isDashSupported,
  isWebmSupported,
} from 'utilities/dash';
import { isFirefox, isMobile } from 'utilities/device';
import {
  documentOnVisibilityChange,
  documentRemoveVisibilityChange,
} from 'utilities/dom';
import { createHlsInstance } from 'utilities/hls';
import { getHlsConfig } from 'utilities/hls-config';
import { error } from 'utilities/loggerx';
import { eqWithinTolerance } from 'utilities/number';
import { getNewTimeSkipRanges } from 'utilities/trimming';
import { canPlayHlsNative, hlsSupported } from 'utilities/video';
import VideoSession from 'utilities/video-session';

import { useFeatureFlagValue } from 'hooks/featureFlag';

import MuteFillerWord from './filler-volume-control/muteFillerWord';
import Captions from '../captions';
import TextToSpeechOverlay from '../../share-video/text-to-speech-overlay';
import {
  MEDIA_ERR_DECODE,
  MEDIA_ERR_NETWORK,
  MEDIA_ERR_SRC_NOT_SUPPORTED,
  READY_STATE_HAVE_FUTURE_DATA,
  TIME_EQ_TOLERANCE,
  TIME_UPDATE_THROTTLE,
} from '../../../constants/video';

import CustomCtaButton from 'components/share-video/cta-button';
import { useClipsState } from 'components/share-video/edit-tools/clips/state';

import './styles.less';

// maximum number of retries for a bad video source
const MAX_SRC_ERROR_RETRIES = 3;

// maximum number of retries for decode errors, which can be due to cache
// expired or automatic decode switching that happens near the beginning of
// playback
const MAX_DECODE_ERROR_RETRIES = 5;

// analytics for a view track (incrementing a view in our UI):
// track one view if 75% of the video is played OR 1 second has been watched
// (whichever comes first)
const TIME_PERCENTAGE_UNTIL_VIEW_TRACKED = 0.75;
// 75% of video play

const TIME_UNTIL_VIEW_TRACKED = 1000; // 1 second of watch duration

// http://probablyprogramming.com/2009/03/15/the-tiniest-gif-ever
const THE_TINIEST_GIF =
  'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

const VideoWithClipsState = props => {
  const clipsState = useClipsState(state => ({
    clips: state.clips,
    focusedClipIndex: state.focusedClipIndex,
    updateFocusedClipIndex: state.updateFocusedClipIndex,
  }));

  const weaveFlagValue = useFeatureFlagValue(ROLLOUT_WEAVE);
  const rolloutWeave = VARIANT.includes(weaveFlagValue);

  if (rolloutWeave) {
    return <Video {...props} clipsState={clipsState} />;
  }

  return <Video {...props} />;
};

class Video extends Component {
  hlsInstance = null;
  dashInstance = null;
  dashContext = {
    muxMonitor: null,
  };
  onVisibilityChangeListener = null;
  timeUpdateInterval = null;
  hasStartedLoading = false;
  canPlay = false;
  player;
  requestCurrentTime;

  state = {
    numAutoplayRetries: 0,
    numSrcRetries: 0,
    numDecodeRetries: 0,
    autoResumeFromTime: 0,
    timeWatched: 0,
    timeWatchedForDurationEvent: 0,
    viewTracked: false,
    videoTouchedOnMobile: false,
    captionText: null,
  };

  render() {
    const {
      autoplay,
      ctaEnabled,
      inCTAFlow,
      EditVideoButtons,
      ViewerVideoButtons,
      ended,
      flippedCamera,
      hasStarted,
      isCameraRecording,
      isMainVideoOnSharePage,
      paused,
      seeking,
      sourceMimeType,
      sourceUrl,
      chromePipEnabled,
      safariPipEnabled,
      captionsVisible,
      currentCaption,
      width,
      height,
      inTrimMode,
    } = this.props;

    const { videoTouchedOnMobile } = this.state;

    const shouldNullSourceUrl =
      sourceUrl && sourceMimeType === M3U8 && !canPlayHlsNative();
    const finalSourceUrl = shouldNullSourceUrl ? null : sourceUrl;

    const flipVideo =
      flippedCamera &&
      isCameraRecording &&
      !chromePipEnabled &&
      !safariPipEnabled;

    const showEditButtons =
      EditVideoButtons &&
      isMainVideoOnSharePage &&
      ((hasStarted && !ended) || inCTAFlow);

    const showViewerButtons =
      ViewerVideoButtons && isMainVideoOnSharePage && hasStarted && !ended;

    const showOldCta = isMainVideoOnSharePage;

    return (
      <div
        className={classNames('video-player-video-container', {
          mobile: isMobile,
          'is-embed': !isMainVideoOnSharePage,
        })}
      >
        {!isMobile && (
          <MuteFillerWord videoRef={this.video} inTrimMode={inTrimMode} />
        )}

        <TextToSpeechOverlay />

        <video
          className={classNames('video-player-video', {
            'flip-video': flipVideo,
          })}
          controls={hasStarted && !ended && isMobile}
          controlsList="nodownload" // Only allows download via our Download button outside of the player
          autoPlay={autoplay}
          ref={videoEl => (this.video = videoEl)}
          playsInline={true}
          preload="auto"
          // NOTE: we set the source on the video element directly since using
          //       <source> elements stops bubbling of the true error from
          //       happening
          //
          //       Do not set the src attribute with shaka-player (DASH), otherwise
          //       it won't be able to detach/attach to the <video> element.
          //       This would break the video reload that happens in the "Trim Video" UI
          src={this.props.sourceMimeType === DASH ? '' : finalSourceUrl}
          onCanPlay={this.onCanPlay}
          onCanPlayThrough={this.onCanPlayThrough}
          // do not allow right-clicks on the video to discourage downloading
          onContextMenu={this.onContextMenu}
          onClick={this.onClick}
          onDoubleClick={this.props.onDoubleClick}
          onDurationChange={this.onDurationChange}
          onEnded={this.onEnded}
          onError={this.onError}
          onLoadedMetadata={this.onLoadedMetadata}
          onLoadStart={this.onLoadStartHandler}
          onPause={this.onPause}
          onPlay={this.onPlay}
          onPlaying={this.props.onPlaying}
          onProgress={this.onProgress}
          onRateChange={this.onRateChange}
          onTimeUpdate={this.onTimeUpdate}
          onVolumeChange={this.onVolumeChange}
          onTouchStart={this.handleMobileVideoTouch}
          onWaiting={this.props.onWaiting}
          tabIndex={-1}
          crossOrigin="anonymous"
          // Note: We need to poster property to a valid image. Otherwise,
          // on an Android WebView, the OS will try to set a default poster
          // image, resulting in a CORS error to be thrown on the HTMLVideoElement.
          poster={
            this.props.videoModel?.video_properties?.recording_version === V6
              ? null
              : THE_TINIEST_GIF
          }
        ></video>
        {showEditButtons && (
          <div className="edit-video-buttons edit-video-buttons-backdrop absolute right:0 text:right">
            <EditVideoButtons
              isCollapsible={(!paused || seeking) && !videoTouchedOnMobile}
            />
          </div>
        )}

        {showViewerButtons && (
          <div
            className={`${
              ctaEnabled
                ? 'viewer-video-buttons-with-cta'
                : 'viewer-video-buttons'
            } edit-video-buttons-backdrop absolute right:0 text:right`}
          >
            <ViewerVideoButtons
              isCollapsible={(!paused || seeking) && !videoTouchedOnMobile}
            />
          </div>
        )}
        {ctaEnabled && hasStarted && !ended && showOldCta && (
          <div
            className={classNames('hover-cta-wrapper', {
              show: paused || videoTouchedOnMobile,
              'embed-top-bar-showing': this.props.showEmbedTopBar,
              'is-embed': !isMainVideoOnSharePage,
            })}
          >
            <CustomCtaButton onClick={this.onCtaClick} />
          </div>
        )}
        {captionsVisible && !isMobile && (
          <Captions
            videoWidth={width}
            videoHeight={height}
            text={currentCaption}
          />
        )}
      </div>
    );
  }

  setupPipObservers() {
    const chromePipSupported = document.pictureInPictureEnabled;
    const safariPipSupported =
      this.video.webkitSupportsPresentationMode &&
      typeof this.video.webkitSetPresentationMode === 'function';

    this.props.updatePipSupported(chromePipSupported || safariPipSupported);

    this.video.addEventListener('enterpictureinpicture', this.onEnterChromePip);
    this.video.addEventListener('leavepictureinpicture', this.onExitChromePip);
    this.video.addEventListener(
      'webkitpresentationmodechanged',
      this.onSafariPipChange
    );
  }

  removePipObservers() {
    this.video.removeEventListener(
      'enterpictureinpicture',
      this.onEnterChromePip
    );

    this.video.removeEventListener(
      'leavepictureinpicture',
      this.onExitChromePip
    );

    this.video.removeEventListener(
      'webkitpresentationmodechanged',
      this.onSafariPipChange
    );
  }

  onCueChange = event => {
    const activeCues = event.target.track.activeCues;
    const text = activeCues[0]?.text;

    this.props.setCurrentCaption(text);
  };

  hideTextTracks = () => {
    for (let idx = 0; idx < this.video.textTracks.length; idx++) {
      this.video.textTracks[idx].mode = 'hidden';
    }
  };

  setupCCObservers = track => {
    if (!track) {
      return;
    }

    track.mode = 'hidden';
    track.addEventListener('cuechange', this.onCueChange);
  };

  removeCCObservers = track => {
    if (!track) {
      return;
    }

    track.removeEventListener('cuechange', this.onCueChange);
  };

  componentDidMount() {
    const {
      isMainVideoOnSharePage,
      videoModel,
      source_part_credentials,
      sourceMimeType,
    } = this.props;

    this.checkRequestedMutations();

    if (isMainVideoOnSharePage && videoModel.current_user_is_owner) {
      window.top.postMessage({ type: SHARE_VIDEO_EL_LOADED }, '*');
    }

    // setup video watched duration analytics flush listeners
    window.addEventListener('beforeunload', this.onUnload);
    window.addEventListener('unload', this.onUnload);

    if (isMobile) {
      // https://stackoverflow.com/a/31039609/696130
      document.addEventListener('pagehide', this.onUnload);

      this.onVisibilityChangeListener = documentOnVisibilityChange(
        this.onVisibilityChange
      );
    }

    if (sourceMimeType === DASH) {
      this.checkAndCreateDashInstance(source_part_credentials);
    } else if (sourceMimeType === M3U8) {
      this.checkAndCreateHlsInstance();
    } else if (sourceMimeType === WEBM && !isWebmSupported()) {
      this.loadFallbackSource();
    }

    this.setupPipObservers();
  }

  componentWillUnmount() {
    this.clearTimeUpdateInterval();

    // remove video watched duration analytics flush listeners
    window.removeEventListener('beforeunload', this.onUnload);
    window.removeEventListener('unload', this.onUnload);
    document.removeEventListener('pagehide', this.onUnload);

    if (this.video && this.mobileTouchTimeout) {
      window.clearTimeout(this.mobileTouchTimeout);
    }

    documentRemoveVisibilityChange(this.onVisibilityChangeListener);

    this.removePipObservers();

    const oldTrack = document.getElementById('active_captions_track');

    if (oldTrack) {
      this.removeCCObservers(oldTrack);
    }
  }

  componentDidUpdate(prevProps) {
    this.checkRequestedMutations();

    const { ended, paused, seeking } = this.video;

    if (!VideoSession.videoRef && this.video) {
      VideoSession.setVideoRef(this.video);
    }

    if (this.timeUpdateInterval != null && !seeking && (ended || paused)) {
      this.clearTimeUpdateInterval();
    } else if (
      this.timeUpdateInterval == null &&
      (seeking || !(ended || paused))
    ) {
      this.setupTimeUpdateInterval();
    }

    if (
      (!prevProps.sourceUrl && this.props.sourceUrl) ||
      (this.props.videoModel?.video_properties?.recording_version === V6 &&
        prevProps.sourceUrl !== this.props.sourceUrl)
    ) {
      if (this.props.sourceMimeType === DASH) {
        this.checkAndCreateDashInstance(this.props.source_part_credentials);
      } else {
        this.checkAndCreateHlsInstance();
      }
    }

    // If we get a new captionsSourceUrl that does not match the previous captionsSourceUrl
    // We need to dynamically add the caption text tracks to the video player as
    // when we have filler word removal we need to be able to dynamically switch
    // out the active caption track. Using a ref on the track component and updating
    // the src is not updating the TextTrack active cues on Firefox.
    if (prevProps.captionsSourceUrl !== this.props.captionsSourceUrl) {
      // Find old track if exist and remove it as well as event listeners
      const oldTrack = document.getElementById('active_captions_track');

      // this.props.setCurrentCaption(null);
      this.removeCCObservers(oldTrack);

      if (oldTrack) {
        oldTrack.remove();
      }

      // Dynamically create the new captions
      const track = document.createElement('track');

      track.setAttribute('id', 'active_captions_track');
      track.kind = 'captions';
      track.label = 'English';
      track.srclang = 'en';
      track.src = this.props.captionsSourceUrl;

      // Add event listeners to new track
      this.setupCCObservers(track);

      this.video.appendChild(track);
    }

    // Safari will show the captions using the default CC implementation
    // unless we do this.
    if (!isMobile) {
      this.hideTextTracks();
    }
  }

  onSafariPipChange = () => {
    this.props.updateSafariPipEnabled(
      this.video.webkitPresentationMode === 'picture-in-picture'
    );
  };

  onEnterChromePip = () => {
    this.props.updateChromePipEnabled(true);
  };

  onExitChromePip = () => {
    this.props.updateChromePipEnabled(false);
  };

  handleMobileVideoTouch = () => {
    if (!isMobile) {
      return;
    }

    this.setState({ videoTouchedOnMobile: true });

    this.mobileTouchTimeout = window.setTimeout(
      () => this.setState({ videoTouchedOnMobile: false }),
      2000
    );
  };

  getMuxMonitorConfig(sourceUrl) {
    const { videoModel, userAnonId } = this.props;

    const getSubProp = url => {
      const sourceTypeFromRegex = {
        'hls raw': /sessions\/raw\/.*?\.m3u8/,
        'hls transcoded': /sessions\/transcoded\/.*?\.m3u8/,
        'hls jit': /sessions\/hls\/.*?\.m3u8/,
        'dash raw': /sessions\/raw\/.*?\.mpd/,
        'mp4 transcoded': /sessions\/transcoded\/.*?\.mp4/,
      };

      for (const [sourceType, regex] of Object.entries(sourceTypeFromRegex)) {
        if (url?.match(regex)) {
          return sourceType;
        }
      }

      return null;
    };

    const getEncodingVariant = video_properties => {
      if (!video_properties) {
        return null;
      }

      const os = video_properties.os || '';
      const os_underscored = os.toLowerCase().replace(/ /g, '_');

      return [os_underscored, video_properties.recording_version]
        .filter(Boolean)
        .join('_');
    };

    const experimentName = videoModel?.processing_information
      ?.instant_editing_enabled
      ? 'avserver'
      : null;

    return {
      debug: false,
      data: {
        env_key: MUX_ENV_KEY,
        video_id: videoModel.id,
        viewer_user_id: userAnonId,
        player_name: isMobile ? 'Native' : 'Loom',
        sub_property_id: getSubProp(sourceUrl),
        experiment_name: experimentName,
        video_encoding_variant: getEncodingVariant(videoModel.video_properties),
      },
    };
  }

  setupHlsMonitor(sendMuxPlaybackStats) {
    // Add stats report based on feature flag
    if (!sendMuxPlaybackStats || !this.hlsInstance) {
      return;
    }

    const muxConfig = this.getMuxMonitorConfig(this.props.plainSourceUrl);

    muxConfig.Hls = Hls;
    muxConfig.hlsjs = this.hlsInstance;

    mux.monitor(this.video, muxConfig);
  }

  setupDashMonitor(player, sendMuxPlaybackStats) {
    // Add stats report based on feature flag
    if (!sendMuxPlaybackStats || !player || !window.initShakaPlayerMux) {
      return;
    }

    this.dashContext.muxMonitor = window.initShakaPlayerMux(
      player,
      this.getMuxMonitorConfig(this.props.plainSourceUrl)
    );
  }

  checkAndCreateDashInstance(partCredentials) {
    const { sourceUrl, videoModel } = this.props;

    if (!sourceUrl || !isDashSupported()) {
      return;
    }

    if (this.dashInstance) {
      // Reset current DASH player
      // http://cdn.dashjs.org/latest/jsdoc/module-MediaPlayer.html#reset__anchor
      this.dashInstance.detach();
    }

    const featureFlags = videoModel.video_feature_flags;
    const sendMuxPlaybackStats = featureFlags[SEND_HLS_PLAYBACK_STATS];

    this.dashInstance = createDashInstance({
      manifestUrl: sourceUrl,
      videoElement: this.video,
      partCredentials,
      beforeLoad: player => {
        this.setupDashMonitor(player, sendMuxPlaybackStats);
      },
      onLoadError: error => {
        if (!this.dashContext.muxMonitor) {
          return;
        }

        this.dashContext.muxMonitor.loadErrorHandler(error);
      },
    });

    this.dashInstance.addEventListener('error', playerError => {
      const { videoModel, sourceMimeType } = this.props;

      error(
        Error('Error from dashjs'),
        { error: playerError.error || playerError },
        {
          video_id: videoModel.id,
          video_type: sourceMimeType,
        }
      );

      this.loadFallbackSource();
    });
  }

  checkAndCreateHlsInstance() {
    const { sourceUrl, sourceMimeType, videoModel, isMainVideoOnSharePage } =
      this.props;

    if (!sourceUrl || sourceMimeType !== M3U8) {
      return;
    }

    if (!hlsSupported()) {
      return;
    }

    const featureFlags = videoModel.video_feature_flags;

    // only start loading video if main video on share page
    const clientConfigOverride = { autoStartLoad: isMainVideoOnSharePage };
    const serverConfigOverride =
      typeof featureFlags[CONFIG_HLS_JS_PLAYER] === 'object'
        ? featureFlags[CONFIG_HLS_JS_PLAYER]
        : {};

    const hlsConfig = getHlsConfig(clientConfigOverride, serverConfigOverride);

    this.hlsInstance = createHlsInstance(sourceUrl, this.video, hlsConfig);

    this.setupHlsMonitor(featureFlags[SEND_HLS_PLAYBACK_STATS]);
  }

  onLoadStartHandler = () => {
    const { autoResumeFromTime } = this.state;
    const { isMainVideoOnSharePage } = this.props;

    if (isMainVideoOnSharePage) {
      this.props.updateShareVideoPlayerReady(true);
      this.props.updateShareVideoIframeLoaded(true);
    } else {
      this.setupPlayerJsAdapter();
    }

    if (autoResumeFromTime) {
      this.video.currentTime = autoResumeFromTime;
      this.video.play();
      this.setState({ autoResumeFromTime: 0 });
    }
  };

  // for Embed.ly && player.js
  setupPlayerJsAdapter = () => {
    const adapter = playerjs.HTML5Adapter(this.video);

    adapter.ready();
  };

  setCurrentTime(currentTime) {
    try {
      this.video.currentTime = currentTime;
    } catch (err) {
      // could happen because video is not ready or because the time is
      // non-finite - warn but don't fail unless it stops playback
      console.warn(`could not set current time ${err}`);
    }
  }

  // this implementation is borrowed from here:
  // https://github.com/videojs/video.js/pull/4591
  setupTimeUpdateInterval() {
    this.timeUpdateInterval = window.setInterval(
      this.onTimeUpdate,
      TIME_UPDATE_THROTTLE
    );
  }

  clearTimeUpdateInterval() {
    window.clearInterval(this.timeUpdateInterval);

    this.timeUpdateInterval = null;
  }

  checkRequestedMutations() {
    // we only want to trigger the code below if this.canPlay has been set,
    // otherwise requests that happen before the video is ready end up being
    // swallowed up, and the app state updates that follow (due to triggers)
    // end up canceling those requests without them actually happening
    // NOTE: we always continue below if we're on the embed page (i.e. not on
    // share page), since for some reason the canPlay event never fires there
    if (this.props.isMainVideoOnSharePage && !this.canPlay) {
      return;
    }

    const {
      requestCurrentTime,
      requestMute,
      requestPause,
      requestPlay,
      requestPlaybackRate,
      requestUnmute,
      requestVolume,
    } = this.props;

    // while the video is trimming, this.video may be null
    if (!this.video) {
      return;
    }

    if (requestVolume != null) {
      this.video.muted = requestVolume === 0;
      this.video.volume = requestVolume;
    }

    if (requestPause && requestPlay) {
      this.video
        .play()
        .then(() => this.video.pause())
        .catch(e => {
          // if this fails, and the error is a DOMException, it means that the
          // video did not play then pause because the user has not interacted
          // with the page, in which case we mute the video and try again. this
          // is a feature of Chrome that prevents videos from autoplaying with
          // sound on.
          if (e instanceof DOMException) {
            this.video.muted = true;
            // muting is async on firefox, so we setTimeout to make sure it
            // happens before we retry playing the video
            setTimeout(() => {
              this.video
                .play()
                .then(() => this.video.pause())
                .catch(() => {
                  /* Do nothing here */
                });
            }, 200);
          }
        });
    } else if (requestPause && this.video.pause != null) {
      this.video.pause();
    } else if (requestPlay && this.video.play != null) {
      const { numAutoplayRetries } = this.state;

      this.video.play().catch(() => {
        if (this.props.hasStarted) {
          return;
        }

        // when forcing autoplay we want to retry a couple of times
        // before pausing.
        // requesting pausing is necessary as we use the redux state
        // as a source of truth to determine if a loader should show.
        // without requesting pause, when autoplay is denied by the browser
        // a loading indicator would hide the play button breaking the player
        // this should be ideally solved by using the HTML video API (this.video)
        // as the source of truth for the video player UI
        if (numAutoplayRetries > 2) {
          this.props.requestVideoPlayerPause();

          return;
        }

        this.setState({ numAutoplayRetries: numAutoplayRetries + 1 });
      });
    }

    if (requestMute) {
      this.video.muted = true;
    } else if (requestUnmute) {
      this.video.muted = false;

      // if we're unmuting but the volume is 0, turn it back on full
      if (this.video.volume === 0) {
        this.video.volume = 1;
      }
    }

    if (requestCurrentTime != null) {
      this.onSeekage(requestCurrentTime);
      this.setCurrentTime(requestCurrentTime);
    }

    if (requestPlaybackRate != null) {
      this.video.playbackRate = requestPlaybackRate;
    }
  }

  getBufferedParts() {
    const { buffered } = this.video;
    const bufferedParts = [];

    for (let part = 0; part < buffered.length; part += 1) {
      bufferedParts.push({
        end: buffered.end(part),
        start: buffered.start(part),
      });
    }

    return bufferedParts;
  }

  trackVideoViewEvent() {
    if (this.state.viewTracked) {
      return;
    }

    this.setState({
      viewTracked: true,
    });

    this.props.onTrackVideoView();
  }

  flushVideoViewDurationEvent() {
    const { timeWatchedForDurationEvent: timeWatched } = this.state;

    if (timeWatched > 0) {
      this.props.onTrackVideoWatchDuration(Math.round(timeWatched / 1000));
      this.setState({ timeWatchedForDurationEvent: 0 });
    }
  }

  onCanPlay = () => {
    // Make sure not to call this twice if has already been called
    if (this.canPlay) {
      return;
    }

    this.canPlay = true;

    if (this.props.isMainVideoOnSharePage) {
      this.props.videoReadyToPlayOnSharePage();
    }

    if ((this.props.isInWaveformFlag || !this.props.inTrimMode) && this.video) {
      this.video.playbackRate = this.props.playbackRate;
    }

    this.checkRequestedMutations();
  };

  onCanPlayThrough = () => {
    this.onProgress();
  };

  onClick = e => {
    e.preventDefault();

    if (this.video.paused) {
      this.video.play().catch(() => {
        /* Do nothing here */
      });
    } else {
      this.video.pause();
    }
  };

  onContextMenu = e => {
    e.preventDefault();

    return false;
  };

  onDimensionChange = (width, height) => {
    if (this.props.onDimensionChange) {
      this.props.onDimensionChange(width, height);
    }
  };

  onDurationChange = () => {
    const { duration } = this.video || {};

    if (this.props.onDurationChange) {
      this.props.onDurationChange(this.video.duration);
    }

    // the duration can be NaN, 0 or Infinity
    // while the video loads the metadata / if it fails
    // this event will fire again once the duration is known
    if (duration && isFinite(duration)) {
      VideoSession.sendEvent('ready', { duration: this.video.duration });
    }
  };

  onSeekage = requestCurrentTime => {
    const { currentTime } = this.video;

    if (Math.abs(requestCurrentTime - currentTime) < 1) {
      return;
    }

    VideoSession.sendEvent('pause', { currentTime });
    VideoSession.sendEvent('play', { currentTime: requestCurrentTime });
  };

  onEnded = () => {
    this.trackVideoViewEvent();

    VideoSession.sendEvent('ended');

    if (this.props.onEnded) {
      this.props.onEnded();
    }
  };

  onCtaClick = () => {
    const { currentTime } = this.video;

    VideoSession.sendEvent('cta', { currentTime });
  };

  retryVideoPlaybackSrcError = errorCode => {
    const { numSrcRetries } = this.state;
    const { sourceMimeType, videoModel } = this.props;

    if (numSrcRetries >= MAX_SRC_ERROR_RETRIES) {
      if (![WEBM, M3U8, DASH].includes(sourceMimeType)) {
        return error(
          Error('Embed Video MediaSrc Error'),
          { message: 'error fetching video src' },
          {
            error_code: errorCode,
            video_id: videoModel.id,
            video_type: sourceMimeType,
          }
        );
      }

      // try MP4
      this.loadFallbackSource();
    }

    // sometimes the media is not ready in Cloudfront even though it's
    // ready in S3, so we catch that network error here and retry
    // https://github.com/videojs/video.js/issues/1805#issuecomment-273671931
    window.setTimeout(() => {
      const currentTime = this.video.currentTime;

      this.setState({ numSrcRetries: numSrcRetries + 1 });

      this.video.pause();
      this.video.load();
      this.setCurrentTime(currentTime);
      this.video.play().catch(() => {
        /* Do nothing here */
      });
    }, 100 * Math.pow(2, numSrcRetries));
  };

  retryVideoPlaybackDecodeError = () => {
    const { numDecodeRetries } = this.state;
    const { sourceMimeType, videoModel } = this.props;

    if (numDecodeRetries >= MAX_DECODE_ERROR_RETRIES) {
      return error(
        Error('Embed Video Decode Error'),
        { message: 'error decoding video' },
        {
          error_code: MEDIA_ERR_DECODE,
          video_id: videoModel.id,
          video_type: sourceMimeType,
        }
      );
    }

    const currentTime = this.video.currentTime;

    this.setState({
      autoResumeFromTime: currentTime,
      numDecodeRetries: numDecodeRetries + 1,
    });

    this.loadFallbackSource();
  };

  onError = () => {
    const { error } = this.video;
    const { code } = error || {}; // sometimes `error` is `null` so we `||`

    // if video is inactive and error is on missing source, do nothing
    if (
      !this.props.videoModel.active_status &&
      code === MEDIA_ERR_SRC_NOT_SUPPORTED
    ) {
      return;
    }

    // This error should be ignored in Firefox when playing v5 sources (DASH)
    if (
      code === MEDIA_ERR_SRC_NOT_SUPPORTED &&
      isFirefox &&
      this.props.sourceMimeType === DASH
    ) {
      return;
    }

    if (this.props.onError) {
      this.props.onError();
    }

    // if the source url is the streamed webm object, it may get deleted while
    // the viewer is watching the video since we delete the streamed S3 object
    // once the non-streamed version has been uploaded, so we should replace with
    // the non-streamed object
    if (this.props.sourceUrl.indexOf('-streamed.webm') > -1) {
      const currentTime = this.video.currentTime;

      return this.loadFallbackSource().then(() => {
        this.setCurrentTime(currentTime);
        this.video.play().catch(() => {
          /* Do nothing here */
        });
      });
    }

    if ([MEDIA_ERR_NETWORK, MEDIA_ERR_SRC_NOT_SUPPORTED].includes(code)) {
      this.retryVideoPlaybackSrcError(code);
    } else if (code === MEDIA_ERR_DECODE) {
      this.retryVideoPlaybackDecodeError();
    }
  };

  loadFallbackSource = () => {
    // Remove existing dash.js instance on error, transitioning to MP4 will fail without this
    if (this.dashInstance) {
      this.player.mux.destroy();
      this.dashInstance.detach();
      this.dashInstance = null;
    }

    // Prevent another request while one is still in progress
    if (this.loadingFallback) {
      return;
    }

    this.loadingFallback = true;

    return new Promise((resolve, reject) => {
      const { onRefetchSource } = this.props;

      const errorHandler = errorCaught => {
        const { videoModel, sourceMimeType } = this.props;

        error(
          errorCaught,
          { message: 'error loading fallback source' },
          {
            video_id: videoModel.id,
            video_type: sourceMimeType,
          }
        );
        this.loadingFallback = false;
        reject(errorCaught);
      };

      try {
        onRefetchSource();
      } catch (err) {
        errorHandler(err);

        return;
      }

      setTimeout(() => {
        const restartPlayback = () => {
          this.video.pause();
          this.video.load();
          this.video.play(); // it may fail if autoplay is not blocked by the browser
        };

        // The player may be stuck in the loading state, attempt to restart the playback
        restartPlayback();
        this.loadingFallback = false;
        resolve();
      }, 200); // arbitrary
    });
  };

  onLoadedMetadata = () => {
    // WARN: Safari may sometimes set these to zero:
    // https://github.com/flowplayer/flowplayer/issues/1228
    // do not consider zero to be a valid value
    if (this.video.videoWidth > 0 && this.video.videoHeight > 0) {
      this.onDimensionChange(this.video.videoWidth, this.video.videoHeight);
    }

    this.onDurationChange();

    // some browsers (on ios) don't trigger canplay event properly, so we trigger that
    // here instead
    this.onCanPlay();
  };

  onPlay = () => {
    if (
      this.hlsInstance &&
      !this.hlsInstance.config.autoStartLoad &&
      !this.hasStartedLoading
    ) {
      this.hlsInstance.startLoad(-1);
      this.hasStartedLoading = true;
    }

    if (this.props.onPlay) {
      this.props.onPlay();
    }

    const { currentTime } = this.video;

    VideoSession.sendEvent('play', { currentTime });

    analytics.track(VIDEO_PLAYER_VIDEO_PLAY_START, {
      currentTime,
      videoId: this.props.videoModel.id,
    });

    if (this.props.fillerWordRemoval === TEXT_AUDIO) {
      analytics.track(VIDEO_PLAYER_WILL_MUTE_FILLER_WORDS, {
        videoId: this.props.videoModel.id,
        fillerWordRemoval: TEXT_AUDIO,
      });
    }
  };

  onPause = () => {
    if (this.props.onPause) {
      this.props.onPause();
    }

    const { currentTime } = this.video;

    VideoSession.sendEvent('pause', { currentTime });

    // Because we request pause/play state in the reducers, sometimes a pause
    // event will happen right after a play and cancel that trigger, which may
    // cause the waiting state to stick around. We prevent bad waiting state by
    // checking for that here:
    //
    // https://stackoverflow.com/a/14270041/696130
    if (
      this.video.readyState >= READY_STATE_HAVE_FUTURE_DATA &&
      this.props.onNotWaiting
    ) {
      this.props.onNotWaiting();
    }
  };

  onProgress = () => {
    if (!this.props.onProgress) {
      return;
    }

    const bufferedParts = this.getBufferedParts();

    this.props.onProgress(bufferedParts);
  };

  onRateChange = () => {
    if (this.props.onRateChange) {
      this.props.onRateChange(this.video.playbackRate);
    }

    const { currentTime, playbackRate } = this.video;

    VideoSession.sendEvent('rate-change', { currentTime, playbackRate });
    analytics.track(PLAYBACK_RATE_CHANGE, {
      playbackRate,
      currentTime,
      videoId: this.props.videoModel.id,
    });
  };

  onTimeUpdate = _throttle(() => {
    const {
      currentTime: lastVideoTime,
      currentTrimRange,
      inTrimMode,
      seeking,
      trimRanges,
      videoModel,
      ended,
      paused,
    } = this.props;

    // Without this line, we would continue to call `onTimeUpdate` continuously.
    // This prevents it from being called over and over if the video is paused
    // or ended.
    if (!seeking && (ended || paused)) {
      this.clearTimeUpdateInterval();
    }

    const shouldSkipUpdateTime =
      !this.canPlay && this.requestCurrentTime == null;

    // this happens sometimes where the ref hasn't been set or is `null` before
    // mounting but thee underlying DOM element exists and updates its time:
    // https://github.com/facebook/react/issues/4533
    const nullVideo = this.video == null;

    if (nullVideo || shouldSkipUpdateTime) {
      return;
    }

    const latestVideoTime = this.video.currentTime;

    // NOTE: we do calculations on timeElapsed in milliseconds to avoid rounding
    //       errors since we want accurate analytics tracking
    const timeElapsed =
      Math.round(latestVideoTime * 1000) - Math.round(lastVideoTime * 1000);

    if (timeElapsed === 0) {
      return;
    }

    // track a video view if one hasn't been tracked already
    if (
      !videoModel.current_user_is_owner &&
      !inTrimMode &&
      !this.state.viewTracked &&
      !seeking &&
      timeElapsed > 0
    ) {
      this.setState({ timeWatched: this.state.timeWatched + timeElapsed });

      const percentagePlayed =
        this.state.timeWatched / (this.video.duration * 1000);

      if (
        this.state.timeWatched >= TIME_UNTIL_VIEW_TRACKED ||
        percentagePlayed >= TIME_PERCENTAGE_UNTIL_VIEW_TRACKED
      ) {
        this.trackVideoViewEvent();
      }
    }

    if (!inTrimMode && !seeking && timeElapsed > 0) {
      this.setState({
        timeWatchedForDurationEvent:
          this.state.timeWatchedForDurationEvent + timeElapsed,
      });
    }

    const isV6Video =
      this.props.videoModel?.video_properties?.recording_version === V6;

    if (!inTrimMode || seeking || (trimRanges.length === 0 && !isV6Video)) {
      return this.props.onTimeUpdate(this.video.currentTime);
    }

    let newTime = lastVideoTime;

    if (this.props.isInWeaveFlag && isV6Video) {
      const { clips, focusedClipIndex } = this.props.clipsState;

      const clipTrimRanges = clips[focusedClipIndex]?.trimRanges;
      const clipDuration = clips[focusedClipIndex]?.duration;

      if (!clipTrimRanges || !clipDuration) {
        return this.props.onTimeUpdate(this.video.currentTime);
      }

      newTime = getNewTimeSkipRanges(
        round(lastVideoTime * 1000, TIME_EQ_TOLERANCE),
        round(latestVideoTime * 1000, TIME_EQ_TOLERANCE),
        clipDuration * 1000,
        clipTrimRanges,
        null
      );
    } else {
      newTime = getNewTimeSkipRanges(
        round(lastVideoTime * 1000, TIME_EQ_TOLERANCE),
        round(latestVideoTime * 1000, TIME_EQ_TOLERANCE),
        this.video.duration * 1000,
        trimRanges,
        currentTrimRange
      );
    }

    if (eqWithinTolerance(newTime, latestVideoTime * 1000, TIME_EQ_TOLERANCE)) {
      return this.props.onTimeUpdate(latestVideoTime);
    }

    // At the end of our play ranges
    if (eqWithinTolerance(newTime, lastVideoTime * 1000, TIME_EQ_TOLERANCE)) {
      // if clips exist and it is not the last clip  and we are at the end of
      // the clip's play ranged then we want to update to the next clip's
      // source url
      if (
        this.props.isInWeaveFlag &&
        isV6Video &&
        this.props.clipsState?.focusedClipIndex !==
          this.props.clipsState?.clips.length - 1
      ) {
        const { clips, focusedClipIndex, updateFocusedClipIndex } =
          this.props.clipsState;
        const nextClipIndex = focusedClipIndex + 1;
        const nextClip = clips[nextClipIndex];

        // update to the next clip index
        updateFocusedClipIndex(nextClipIndex);

        this.props.updateVideoPlayerSourceFromClip({
          sourceUrl: nextClip?.source_url,
          sourceMimeType: nextClip?.source_mime_type,
          partCredentials: nextClip?.source_part_credentials,
          sourceUrlIsTranscoded: nextClip?.source_url_is_transcoded,
          autoplay: !paused,
        });

        analytics.track(CLIP_VIDEO_SOURCE_URL_UPDATED, {
          id: videoModel.id,
          clipId: nextClip?.id,
          event_type: !paused ? 'autoplay' : 'click',
        });
      } else {
        // else if there are no clips or if there are clips and
        // it is the last clip - we are at the end so need to end
        this.video.pause();
        this.props.updateVideoPlayerEnded();
      }
    } else {
      this.setCurrentTime(newTime / 1000);
    }
  }, TIME_UPDATE_THROTTLE);

  onVolumeChange = () => {
    if (this.props.onVolumeChange) {
      this.props.onVolumeChange(this.video.volume);
    }

    const { currentTime, volume } = this.video;

    VideoSession.sendEvent('volume-change', { currentTime, volume });

    const muted = this.video.muted || this.video.volume === 0;

    if (muted && this.props.onMute) {
      this.props.onMute();
    } else if (this.video.volume > 0 && this.props.onUnmute) {
      this.props.onUnmute();
    }
  };

  onVisibilityChange = visible => {
    if (!visible) {
      this.onUnload();
    }
  };

  onUnload = () => {
    this.flushVideoViewDurationEvent();
  };
}

const mapStateToProps = state => {
  const { feature_flags: featureFlags, video_player } = state;
  const { cta_flow: inCTAFlow } = state.editor;

  const userAnonId = selectUserId(state);
  const isInWaveformFlag = featureFlags[ROLLOUT_WAVEFORM]?.value === 'show-new';
  const isInWeaveFlag = VARIANT.includes(featureFlags[ROLLOUT_WEAVE]?.value);

  return {
    isMainVideoOnSharePage: selectIsMainVideoOnSharePage(state),
    chromePipEnabled: video_player.chromePipEnabled,
    safariPipEnabled: video_player.safariPipEnabled,
    userAnonId,
    source_part_credentials: video_player.source_part_credentials,
    inCTAFlow,
    currentCaption: video_player.currentCaption,
    fillerWordRemoval: video_player.fillerWordRemoval,
    plainSourceUrl: video_player.plainSourceUrl,
    playbackRate: video_player.playback_rate,
    isInWaveformFlag,
    isInWeaveFlag,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateChromePipEnabled: chromePipEnabled =>
      dispatch(updateChromePipEnabled(chromePipEnabled)),
    updateSafariPipEnabled: safariPipEnabled =>
      dispatch(updateSafariPipEnabled(safariPipEnabled)),
    updatePipSupported: pipSupported =>
      dispatch(updatePipSupported(pipSupported)),
    updateShareVideoIframeLoaded: loaded =>
      dispatch(updateShareVideoIframeLoaded(loaded)),
    updateShareVideoPlayerReady: playerReady =>
      dispatch(updateShareVideoPlayerReady(playerReady)),
    videoReadyToPlayOnSharePage: () => dispatch(videoReadyToPlayOnSharePage()),
    setCurrentCaption: caption => dispatch(setCurrentCaption(caption)),
    requestVideoPlayerPause: () => dispatch(requestVideoPlayerPause()),
    updateVideoPlayerEnded: () => dispatch(updateVideoPlayerEnded()),
    updateVideoPlayerSourceFromClip: ({
      sourceUrl,
      sourceMimeType,
      partCredentials,
      sourceUrlIsTranscoded,
      autoplay,
    }) =>
      dispatch(
        updateVideoPlayerSourceFromClip({
          sourceUrl,
          sourceMimeType,
          partCredentials,
          sourceUrlIsTranscoded,
          autoplay,
        })
      ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(VideoWithClipsState);
